// Vendor Management page of the Purchase panel (Vendors in its sub-nav):
// summary cards, one filter bar, the vendor table and pagination. Anyone with
// the Vendors link can browse and view a vendor; adding, editing and
// (de)activating stay admin-only, as the backend requires.
import React, { useCallback, useEffect, useState } from 'react';
import {
    Plus,
    Search,
    Eye,
    SquarePen,
    MoreVertical,
    Phone,
    Store,
    CheckCircle2,
    PauseCircle,
    Box,
    Pill,
    ShoppingBasket,
    Wrench,
    BookOpen,
    FlaskConical,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { api } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import { UserTypes, normalizeUserRole } from '../../../constants/userTypes';
import showToast from '../../../utils/toast';
import { confirmDialog } from '../../../utils/dialogs';
import VendorFormModal from '../../shop/VendorFormModal';
import './VendorsView.css';

const VENDOR_CATEGORIES = ['ISF Shop', 'Medicines', 'Consumables', 'Infra', 'Other'];
const PAGE_SIZES = [10, 20, 50];
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Category pill colour, and the shop icon (when no icon was uploaded) of a
// vendor whose first category it is.
const CATEGORY_STYLE = {
    'ISF Shop': { tone: 'green', Icon: BookOpen },
    Medicines: { tone: 'pink', Icon: Pill },
    Consumables: { tone: 'blue', Icon: ShoppingBasket },
    Infra: { tone: 'yellow', Icon: Wrench },
    Other: { tone: 'purple', Icon: FlaskConical },
};

const errorMessageOf = (err, fallback) =>
    err.response?.data?.error || err.response?.data?.message || err.message || fallback;

// "09:00" -> "9:00 AM"
const formatTime = (value) => {
    const [hours, minutes] = value.split(':').map(Number);
    return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
};

// "All days", a run of days as "Mon – Sat", otherwise the list.
const formatDays = (vendor) => {
    const days = vendor.workingDays || [];
    if (vendor.openAllDays || days.length === WEEK_DAYS.length) return 'All days';
    if (!days.length) return '';
    const indexes = days.map((day) => WEEK_DAYS.indexOf(day)).sort((a, b) => a - b);
    const isRun = indexes.every((index, i) => i === 0 || index === indexes[i - 1] + 1);
    return isRun && days.length > 2
        ? `${WEEK_DAYS[indexes[0]]} – ${WEEK_DAYS[indexes[indexes.length - 1]]}`
        : indexes.map((index) => WEEK_DAYS[index]).join(', ');
};

const getPageButtons = (current, pageCount) => {
    const start = Math.max(1, Math.min(current - 2, pageCount - 4));
    const end = Math.min(pageCount, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export default function VendorsView() {
    const { user } = useAuth();
    const isAdmin = normalizeUserRole(user?.role) === UserTypes.ADMIN;

    const [vendors, setVendors] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, categories: 0 });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [formState, setFormState] = useState(null); // { vendor, readOnly }; vendor null = Add Vendor
    const [menuVendorId, setMenuVendorId] = useState(null);
    // Vendors whose uploaded shop icon failed to load show the category icon.
    const [brokenIcons, setBrokenIcons] = useState({});

    const fetchVendors = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: pageSize };
            if (search.trim()) params.search = search.trim();
            if (statusFilter !== 'all') params.active = statusFilter === 'active' ? 'true' : 'false';
            if (categoryFilter) params.category = categoryFilter;

            const response = await api.get('/api/v2/vendors', { params });
            if (!response.data?.success) throw new Error(response.data?.error || 'Failed to load vendors');

            const list = response.data.vendors || [];
            setVendors(list);
            setTotal(response.data.total ?? list.length);
        } catch (err) {
            const message = errorMessageOf(err, 'Failed to load vendors');
            setError(message);
            setVendors([]);
            setTotal(0);
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, search, statusFilter, categoryFilter]);

    // Summary cards count every vendor, whatever the filters. The list API caps
    // a page at 100 vendors, which is plenty for Categories Covered.
    const fetchStats = useCallback(async () => {
        try {
            const [all, active] = await Promise.all([
                api.get('/api/v2/vendors', { params: { limit: 100 } }),
                api.get('/api/v2/vendors', { params: { limit: 1, active: 'true' } }),
            ]);
            const allTotal = all.data?.total ?? 0;
            const activeTotal = active.data?.total ?? 0;
            setStats({
                total: allTotal,
                active: activeTotal,
                inactive: allTotal - activeTotal,
                categories: new Set((all.data?.vendors || []).flatMap((v) => v.categories || [])).size,
            });
        } catch (err) {
            // The cards keep their last counts; the table reports load errors.
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(fetchVendors, search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [fetchVendors, search]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Close the row menu on any click outside it.
    useEffect(() => {
        if (!menuVendorId) return undefined;
        const close = () => setMenuVendorId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuVendorId]);

    const refresh = () => {
        fetchVendors();
        fetchStats();
    };

    const changeFilter = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const handleFormSubmit = async (payload) => {
        const editing = formState?.vendor?._id;
        try {
            if (editing) {
                await api.put(`/api/v2/vendors/${editing}`, payload);
                showToast('Vendor updated', 'success');
            } else {
                await api.post('/api/v2/vendors', payload);
                showToast('Vendor created', 'success');
            }
            setFormState(null);
            refresh();
        } catch (err) {
            showToast(errorMessageOf(err, 'Failed to save vendor'), 'error');
        }
    };

    const toggleVendorStatus = async (vendor) => {
        const nextActive = !vendor.active;
        const confirmed = await confirmDialog(
            `Are you sure you want to ${nextActive ? 'activate' : 'deactivate'} vendor "${vendor.name}"?`
        );
        if (!confirmed) return;

        try {
            await api.put(`/api/v2/vendors/${vendor._id}`, { active: nextActive });
            showToast(`Vendor ${nextActive ? 'activated' : 'deactivated'}`, 'success');
            refresh();
        } catch (err) {
            showToast(errorMessageOf(err, 'Failed to update vendor'), 'error');
        }
    };

    const cards = [
        { key: 'total', title: 'Total Vendors', value: stats.total, Icon: Store, tone: 'blue' },
        { key: 'active', title: 'Active Vendors', value: stats.active, Icon: CheckCircle2, tone: 'green' },
        { key: 'inactive', title: 'Inactive Vendors', value: stats.inactive, Icon: PauseCircle, tone: 'orange' },
        { key: 'categories', title: 'Categories Covered', value: stats.categories, Icon: Box, tone: 'purple' },
    ];

    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const firstIndex = (page - 1) * pageSize;
    const columnCount = 8;

    const renderRowMenu = (vendor) => (
        <div className="vv-menu" role="menu" onClick={(e) => e.stopPropagation()}>
            {isAdmin && (
                <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                        setMenuVendorId(null);
                        toggleVendorStatus(vendor);
                    }}
                >
                    {vendor.active ? 'Deactivate vendor' : 'Activate vendor'}
                </button>
            )}
            {vendor.googleMapLink ? (
                <a role="menuitem" href={vendor.googleMapLink} target="_blank" rel="noopener noreferrer" onClick={() => setMenuVendorId(null)}>
                    Open in Google Maps
                </a>
            ) : (
                <button type="button" role="menuitem" disabled>No Google Map link</button>
            )}
            {vendor.phone && (
                <a role="menuitem" href={`tel:${vendor.phone}`} onClick={() => setMenuVendorId(null)}>
                    Call vendor
                </a>
            )}
        </div>
    );

    return (
        <div className="vv-root">
            <div className="vv-header">
                <div>
                    <p className="vv-breadcrumb">
                        Purchase <span aria-hidden="true">›</span> <strong>Vendor Management</strong>
                    </p>
                    <h1>Vendor Management</h1>
                    <p className="vv-subtitle">Manage all vendors for ISF purchases.</p>
                </div>
                {isAdmin && (
                    <button type="button" className="vv-add-btn" onClick={() => setFormState({ vendor: null, readOnly: false })}>
                        <Plus size={20} /> Add Vendor
                    </button>
                )}
            </div>

            <div className="vv-cards">
                {cards.map((card) => (
                    <div key={card.key} className="vv-card">
                        <span className={`vv-card-icon vv-tone-${card.tone}`}>
                            <card.Icon size={24} aria-hidden="true" />
                        </span>
                        <span className="vv-card-text">
                            <span className="vv-card-title">{card.title}</span>
                            <span className={`vv-card-value vv-value-${card.tone}`}>{card.value}</span>
                        </span>
                    </div>
                ))}
            </div>

            <div className="vv-panel">
                <div className="vv-toolbar">
                    <div className="vv-search">
                        <Search size={18} className="vv-search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            placeholder="Search by vendor or shop name..."
                            value={search}
                            onChange={changeFilter(setSearch)}
                            aria-label="Search vendors"
                        />
                    </div>

                    <label className="vv-filter">
                        <span>Category</span>
                        <select value={categoryFilter} onChange={changeFilter(setCategoryFilter)}>
                            <option value="">All Categories</option>
                            {VENDOR_CATEGORIES.map((category) => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </label>

                    <label className="vv-filter">
                        <span>Status</span>
                        <select value={statusFilter} onChange={changeFilter(setStatusFilter)}>
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </label>

                    <label className="vv-filter">
                        <span>Show</span>
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            {PAGE_SIZES.map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className="vv-table-wrapper">
                    <table className="vv-table">
                        <thead>
                            <tr>
                                <th>Sr No</th>
                                <th>Shop Name</th>
                                <th>Vendor Name</th>
                                <th>Contact Number</th>
                                <th>Categories Supplied</th>
                                <th>Shop Timing</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={columnCount} className="vv-empty">Loading vendors...</td></tr>
                            ) : error ? (
                                <tr><td colSpan={columnCount} className="vv-empty">{error}</td></tr>
                            ) : vendors.length === 0 ? (
                                <tr><td colSpan={columnCount} className="vv-empty">No vendors found</td></tr>
                            ) : (
                                vendors.map((vendor, index) => {
                                    const shopStyle = CATEGORY_STYLE[vendor.categories?.[0]];
                                    const ShopIcon = shopStyle?.Icon || Store;
                                    const days = formatDays(vendor);
                                    return (
                                        <tr key={vendor._id}>
                                            <td>{firstIndex + index + 1}</td>
                                            <td>
                                                <span className="vv-shop">
                                                    {vendor.shopIcon && !brokenIcons[vendor._id] ? (
                                                        <img
                                                            className="vv-shop-img"
                                                            src={vendor.shopIcon}
                                                            alt=""
                                                            onError={() => setBrokenIcons((prev) => ({ ...prev, [vendor._id]: true }))}
                                                        />
                                                    ) : (
                                                        <span className={`vv-shop-icon vv-text-${shopStyle?.tone || 'blue'}`} aria-hidden="true">
                                                            <ShopIcon size={20} />
                                                        </span>
                                                    )}
                                                    <span className="vv-shop-name">{vendor.name}</span>
                                                </span>
                                            </td>
                                            <td>{vendor.vendorName || '—'}</td>
                                            <td>
                                                {vendor.phone ? (
                                                    <span className="vv-phone">
                                                        <Phone size={15} aria-hidden="true" /> {vendor.phone}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                <span className="vv-pills">
                                                    {(vendor.categories || []).map((category) => (
                                                        <span key={category} className={`vv-pill vv-pill-${CATEGORY_STYLE[category]?.tone || 'blue'}`}>
                                                            {category}
                                                        </span>
                                                    ))}
                                                    {!vendor.categories?.length && '—'}
                                                </span>
                                            </td>
                                            <td>
                                                {vendor.openTime && vendor.closeTime
                                                    ? `${formatTime(vendor.openTime)} – ${formatTime(vendor.closeTime)}`
                                                    : '—'}
                                                {days && <span className="vv-days">{days}</span>}
                                            </td>
                                            <td>
                                                <label className={`vv-switch ${isAdmin ? '' : 'vv-switch-locked'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(vendor.active)}
                                                        onChange={() => toggleVendorStatus(vendor)}
                                                        disabled={!isAdmin}
                                                        aria-label={vendor.active ? 'Deactivate vendor' : 'Activate vendor'}
                                                    />
                                                    <span className="vv-switch-track" aria-hidden="true" />
                                                    <span className={vendor.active ? 'vv-status-active' : 'vv-status-inactive'}>
                                                        {vendor.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </label>
                                            </td>
                                            <td>
                                                <span className="vv-actions">
                                                    <button
                                                        type="button"
                                                        title="View vendor"
                                                        aria-label="View vendor"
                                                        onClick={() => setFormState({ vendor, readOnly: true })}
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            type="button"
                                                            title="Edit vendor"
                                                            aria-label="Edit vendor"
                                                            onClick={() => setFormState({ vendor, readOnly: false })}
                                                        >
                                                            <SquarePen size={18} />
                                                        </button>
                                                    )}
                                                    <span className="vv-menu-anchor">
                                                        <button
                                                            type="button"
                                                            className="vv-more"
                                                            title="More actions"
                                                            aria-label="More actions"
                                                            aria-haspopup="menu"
                                                            aria-expanded={menuVendorId === vendor._id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMenuVendorId(menuVendorId === vendor._id ? null : vendor._id);
                                                            }}
                                                        >
                                                            <MoreVertical size={18} />
                                                        </button>
                                                        {menuVendorId === vendor._id && renderRowMenu(vendor)}
                                                    </span>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && total > 0 && (
                    <div className="vv-pagination">
                        <span className="vv-pagination-info">
                            Showing {firstIndex + 1}–{Math.min(firstIndex + pageSize, total)} of {total} vendors
                        </span>
                        <div className="vv-pagination-controls">
                            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
                                <ChevronLeft size={16} />
                            </button>
                            {getPageButtons(page, pageCount).map((num) => (
                                <button
                                    key={num}
                                    type="button"
                                    className={num === page ? 'active' : ''}
                                    onClick={() => setPage(num)}
                                    aria-current={num === page ? 'page' : undefined}
                                >
                                    {num}
                                </button>
                            ))}
                            <button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)} aria-label="Next page">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {formState && (
                <VendorFormModal
                    vendor={formState.vendor}
                    readOnly={formState.readOnly}
                    onClose={() => setFormState(null)}
                    onSubmit={handleFormSubmit}
                />
            )}
        </div>
    );
}
